import type { UseHeadInput } from '@unhead/vue'

/** The one person behind the project — reused by the article byline and the Person graph. */
export const AUTHOR = {
  name: 'Eduardo Airaudo',
  linkedin: 'https://www.linkedin.com/in/eduardo-airaudo/',
  github: 'https://github.com/eduair94',
} as const

interface SeoArticle {
  /** ISO datetime the content was first published. */
  publishedTime?: string
  /** ISO datetime of the last substantive edit. */
  modifiedTime?: string
  /** Content section shown to crawlers, e.g. "Investigaciones". */
  section?: string
}

interface SeoInput {
  title: string
  description: string
  /** Absolute or root-relative path for the canonical. Defaults to current route. */
  path?: string
  /** Structured data. Emitted as-is inside a JSON-LD script tag (one node or an array of nodes). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  /** Detail pages that no longer exist upstream shouldn't be indexed. */
  noindex?: boolean
  /** Explicit image override (root-relative). Skips the dynamic generator. */
  image?: string
  /** Eyebrow label burned into the dynamic OG image, e.g. "Contrato", "Investigación". */
  kicker?: string
  /** A short headline figure burned into the dynamic OG image, e.g. a formatted peso amount. */
  stat?: string
  /** 'article' emits article:* OG tags and a byline. Default 'website'. */
  type?: 'website' | 'article'
  article?: SeoArticle
  /**
   * Index this page in the DEFAULT locale only; `noindex, follow` elsewhere.
   *
   * For the entity detail families, whose body is Spanish source data wrapped in
   * translated chrome. The name, the organism, the item description and every
   * figure are identical in `/en`, so indexing both yields ~100k near-duplicate
   * pairs answering no English query. The translated surfaces — the directories,
   * the investigations, the analytics pages, /about — do NOT set this: they are
   * genuinely translated and there are about fifty of them.
   */
  defaultLocaleOnly?: boolean
}

/**
 * One place that decides what every page tells a crawler.
 *
 * The site previously shipped a single hard-coded title and
 * description for all ten routes, so every contract, supplier and
 * agency page was a duplicate to Google. Each page now states its own
 * title, description and canonical, and detail pages carry JSON-LD so
 * the facts (who bought, who sold, how much) are machine-readable.
 *
 * `image` never falls back to a missing static file: unless a page passes
 * an explicit `image`, every page gets a purpose-built, on-brand OG image
 * rendered at `/api/og.png` from its own title — a generic share preview
 * was worse than none.
 */
export function useSeo(input: MaybeRefOrGetter<SeoInput>) {
  const route = useRoute()
  const config = useRuntimeConfig()
  const { locale, t } = useI18n()

  const resolved = computed(() => toValue(input))

  const siteUrl = (config.public.siteUrl as string) || ''

  /**
   * WARNING: `?page=` must survive into the canonical.
   *
   * Every page of every directory used to canonicalise to page one, so each
   * paginated URL told Google "I am a duplicate, index the other one instead".
   * That silently cancels the crawlable `<a href>` pagers in `DataPager`: the
   * crawler follows the link and is then told the destination does not count.
   * Page 2..N of a directory is genuinely different content and gets its own
   * canonical.
   *
   * ONLY `page` is carried. `search`, `sort`, `rubro` and the filter params stay
   * out on purpose — those views are near-duplicates of the unfiltered list and
   * already pass `noindex`, so folding them onto the clean URL is correct.
   */
  /**
   * The locale segment, for a canonical that agrees with the page's own hreflang.
   *
   * WARNING: build it by CONCATENATION, never with `localePath()`. That resolves
   * through the router and re-encodes the params, which would turn a supplier id
   * back into `/suppliers/R%2F210002980010` and re-break the sitemap-vs-canonical
   * match. Pages pass a locale-LESS path (`app/context.md` claimed `useSeo` ran
   * `localePath()` on it; it never did), and the strategy is
   * `prefix_except_default` with `es` as default, so the prefix is `/en` or ''.
   */
  const localePrefix = computed(() => (locale.value === 'es' ? '' : `/${locale.value}`))

  const canonical = computed(() => {
    const path = resolved.value.path ?? route.path
    // `route.path` already carries the prefix; an explicit `path` does not.
    const prefixed = resolved.value.path === undefined
      ? path
      : `${localePrefix.value}${path}`
    const base = `${siteUrl}${prefixed}`.replace(/([^:])\/{2,}/g, '$1/')
    // A page that hands us a path with its own query string owns it entirely.
    if (path.includes('?')) return base
    const page = Number(route.query.page)
    if (!Number.isInteger(page) || page <= 1) return base
    return `${base}?page=${page}`
  })

  const brand = computed(() => t('brand.name'))

  // The site name is already in the brand-level title; repeating it on
  // every page just eats the SERP character budget.
  const fullTitle = computed(() => {
    const base = resolved.value.title
    return base.includes(brand.value) ? base : `${base} · ${brand.value}`
  })

  const image = computed(() => {
    if (resolved.value.image) return `${siteUrl}${resolved.value.image}`
    const params = new URLSearchParams({ title: resolved.value.title })
    if (resolved.value.kicker) params.set('kicker', resolved.value.kicker)
    if (resolved.value.stat) params.set('stat', resolved.value.stat)
    if (locale.value !== 'es') params.set('locale', locale.value)
    return `${siteUrl}/api/og.png?${params.toString()}`
  })

  const isArticle = computed(() => resolved.value.type === 'article')

  useHead(() => {
    const meta: NonNullable<UseHeadInput['meta']> = [
      { name: 'description', content: resolved.value.description },
      { property: 'og:title', content: fullTitle.value },
      { property: 'og:description', content: resolved.value.description },
      { property: 'og:url', content: canonical.value },
      { property: 'og:type', content: isArticle.value ? 'article' : 'website' },
      { property: 'og:site_name', content: brand.value },
      { property: 'og:locale', content: locale.value === 'es' ? 'es_UY' : 'en_US' },
      { property: 'og:image', content: image.value },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: fullTitle.value },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: fullTitle.value },
      { name: 'twitter:description', content: resolved.value.description },
      { name: 'twitter:image', content: image.value },
    ]

    const head: UseHeadInput = {
      title: fullTitle.value,
      link: [{ rel: 'canonical', href: canonical.value }],
      meta,
    }

    // `follow` in both cases: the page still passes authority to what it links,
    // it just does not compete for a result of its own.
    if (resolved.value.noindex || (resolved.value.defaultLocaleOnly && locale.value !== 'es')) {
      meta.push({ name: 'robots', content: 'noindex, follow' })
    }

    // Byline. Only on authored content (investigations, curros, recopilatorios,
    // about) — a directory or dashboard page has no "author" in any meaningful
    // sense, and repeating the tag everywhere would dilute it.
    if (isArticle.value) {
      meta.push(
        { name: 'author', content: AUTHOR.name },
        { property: 'article:author', content: AUTHOR.linkedin },
      )
      head.link!.push({ rel: 'author', href: AUTHOR.linkedin })
      const art = resolved.value.article
      if (art?.publishedTime) meta.push({ property: 'article:published_time', content: art.publishedTime })
      if (art?.modifiedTime) meta.push({ property: 'article:modified_time', content: art.modifiedTime })
      if (art?.section) meta.push({ property: 'article:section', content: art.section })
    }

    if (resolved.value.jsonLd) {
      head.script = [{
        type: 'application/ld+json',
        innerHTML: JSON.stringify(resolved.value.jsonLd),
      }]
    }

    return head
  })

  return { canonical, fullTitle }
}

/** The publisher identity reused across every JSON-LD graph. */
export function useOrgLd() {
  const config = useRuntimeConfig()
  const siteUrl = config.public.siteUrl as string
  return {
    '@type': 'Organization',
    'name': 'Con la tuya, contribuyente',
    '@id': `${siteUrl}/#org`,
    'url': siteUrl,
  }
}

/** The person behind the project. `sameAs` is what lets Google (and LinkedIn) tie the byline to the profile. */
export function usePersonLd() {
  const config = useRuntimeConfig()
  const siteUrl = config.public.siteUrl as string
  return {
    '@type': 'Person',
    '@id': `${siteUrl}/about#person`,
    'name': AUTHOR.name,
    'url': `${siteUrl}/about`,
    'sameAs': [AUTHOR.linkedin, AUTHOR.github],
  }
}

/** A BreadcrumbList node for a detail page. `items` is root → leaf; a leaf without `path` is the current page. */
export function useBreadcrumbLd(items: { name: string, path?: string }[]) {
  const config = useRuntimeConfig()
  const siteUrl = config.public.siteUrl as string
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((it, i) => ({
      '@type': 'ListItem',
      'position': i + 1,
      'name': it.name,
      ...(it.path ? { item: `${siteUrl}${it.path}` } : {}),
    })),
  }
}
